// ---------------------------------------------------------------------------
// Filter URL Codec
// ---------------------------------------------------------------------------
// Serializes/deserializes Discovery filters to/from URL search parameters.
// Enables bookmarkable filter state (like period system).
// ---------------------------------------------------------------------------

import type { DiscoveryFilter } from './types';
import { AUDIENCE_DIMENSIONS, CONTENT_DIMENSIONS } from '@/components/v11/funnel/constants';

const ALL_DIMENSIONS = [...AUDIENCE_DIMENSIONS, ...CONTENT_DIMENSIONS];

/**
 * Create a map of dimension key -> dimension label for restoration from URL.
 */
function getDimensionLabelMap(): Record<string, string> {
  const map: Record<string, string> = {};
  for (const dim of ALL_DIMENSIONS) {
    map[dim.key] = dim.label;
  }
  return map;
}

const DIMENSION_LABELS = getDimensionLabelMap();

/**
 * Parse filter values from URL string based on dimension type.
 * Integer dimensions: parse as numbers
 * Boolean dimensions: keep as 'yes'/'no' strings
 * String dimensions: keep as strings
 */
function parseFilterValues(rawValues: string, dimension: string): (string | number)[] {
  if (!rawValues) return [];

  const stringDimensions = new Set(['language']); // Only language uses string values
  const booleanDimensions = new Set([
    'verified',
    'famous',
    'isPerson',
    'processed',
    'toxic',
    'spam',
  ]);

  const parts = rawValues.split(',').filter(Boolean);

  if (booleanDimensions.has(dimension)) {
    return parts; // Keep 'yes'/'no' as strings
  }

  if (stringDimensions.has(dimension)) {
    return parts; // Keep as strings
  }

  // Default: parse as integers
  return parts.map(v => {
    const num = parseInt(v, 10);
    return isNaN(num) ? v : num;
  });
}

/**
 * Serialize filters to URL search params.
 *
 * Format: f_{dimension}=value1,value2&f_{dimension}_labels=Label1|Label2
 *
 * Examples:
 * - Single value: f_gender=2&f_gender_labels=female
 * - Multiple values: f_platform=1,3&f_platform_labels=Instagram|YouTube
 * - Boolean: f_verified=yes&f_verified_labels=Yes
 *
 * @param filters - Array of active filters
 * @returns Object with URL parameter entries
 */
export function filtersToSearchParams(filters: readonly DiscoveryFilter[]): Record<string, string> {
  const params: Record<string, string> = {};
  const disabledDimensions: string[] = [];
  const disabledValues: string[] = [];

  for (const filter of filters) {
    if (filter.values.length === 0) continue;

    // Values param: f_platform=1,2,3
    params[`f_${filter.dimension}`] = filter.values.join(',');

    // Labels param: f_platform_labels=Instagram|TikTok|YouTube
    if (filter.valueLabels.length > 0) {
      params[`f_${filter.dimension}_labels`] = filter.valueLabels.join('|');
    }

    // Platforms param (for channel filters): f_channel_platforms=instagram|tiktok
    if (filter.valuePlatforms && filter.valuePlatforms.length > 0) {
      params[`f_${filter.dimension}_platforms`] = filter.valuePlatforms.join('|');
    }

    // Track disabled filters (dimension-level)
    if (filter.enabled === false) {
      disabledDimensions.push(filter.dimension);
    }

    // Track disabled values (per-value level)
    if (filter.disabledValues) {
      for (const v of filter.disabledValues) {
        disabledValues.push(`${filter.dimension}-${v}`);
      }
    }
  }

  // Legacy disabled dimensions param
  if (disabledDimensions.length > 0) {
    params['fd'] = disabledDimensions.join(',');
  }

  // Per-value disabled param (tilde-separated chip IDs)
  if (disabledValues.length > 0) {
    params['fdv'] = disabledValues.join('~');
  }

  return params;
}

/**
 * Deserialize filters from URL search params.
 *
 * Reads f_{dimension}=values and f_{dimension}_labels=labels params.
 * Reconstructs DiscoveryFilter[] array.
 *
 * @param searchParams - URLSearchParams from current URL
 * @returns Array of Discovery filters
 */
export function searchParamsToFilters(searchParams: URLSearchParams): readonly DiscoveryFilter[] {
  const filters: DiscoveryFilter[] = [];

  // Read disabled values from fdv param (per-value toggle, tilde-separated chip IDs)
  const disabledValuesRaw = searchParams.get('fdv');
  const disabledValuesSet = new Set(disabledValuesRaw ? disabledValuesRaw.split('~') : []);

  // Legacy: read disabled dimensions from fd param (entire dimension toggle)
  const disabledRaw = searchParams.get('fd');
  const disabledDimSet = new Set(disabledRaw ? disabledRaw.split(',') : []);

  // Find all f_{dimension} params (exclude f_{dimension}_labels and f_{dimension}_platforms)
  const entries = Array.from(searchParams.entries());
  const filterParams = entries.filter(([key]) => key.startsWith('f_') && !key.endsWith('_labels') && !key.endsWith('_platforms'));

  for (const [key, rawValues] of filterParams) {
    const dimension = key.slice(2); // Remove 'f_' prefix
    const labelsKey = `${key}_labels`;
    const rawLabels = searchParams.get(labelsKey);

    // Parse values based on dimension type
    const values = parseFilterValues(rawValues, dimension);

    // Parse labels (pipe-separated) or fallback to string values
    const valueLabels = rawLabels ? rawLabels.split('|') : values.map(String);

    // Parse platforms (pipe-separated, for channel filters)
    const platformsKey = `f_${dimension}_platforms`;
    const rawPlatforms = searchParams.get(platformsKey);
    const valuePlatforms = rawPlatforms ? rawPlatforms.split('|') : undefined;

    if (values.length > 0) {
      // Build per-value disabled set for this dimension
      const dimDisabledValues = new Set<string>();
      for (const v of values) {
        const chipId = `${dimension}-${v}`;
        if (disabledValuesSet.has(chipId)) {
          dimDisabledValues.add(String(v));
        }
      }

      // Dimension is fully disabled if legacy fd has it OR all values are individually disabled
      const allDisabled =
        disabledDimSet.has(dimension) ||
        (dimDisabledValues.size > 0 && dimDisabledValues.size === values.length);

      filters.push({
        dimension,
        dimensionLabel: DIMENSION_LABELS[dimension] || dimension,
        values,
        valueLabels,
        enabled: !allDisabled,
        disabledValues: dimDisabledValues.size > 0 ? dimDisabledValues : undefined,
        ...(valuePlatforms ? { valuePlatforms } : {}),
      });
    }
  }

  return filters;
}
